import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Provider } from 'react-redux';
import { router } from 'components/routes/Router';
import { ErrorBoundary } from 'components/molecules/ErrorBoundary';
import { SubscriptionExpire } from 'components/organisms/SubscriptionExpire';
import { ScriptLoader } from 'components/molecules/ScriptLoader';
import configureStore from 'config/store';
import { ToastContainer, Zoom } from 'react-toastify';
import FullScreenLoader from 'components/Loader/FullScreenLoader';
import UserProvider from 'contexts/user.context';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'lib/queryClient';
import { WebSocketProvider } from 'contexts/webSocketcontext';
import { WS_API_URL } from 'config/constants';
import { initAmplitude } from './utils/Analytics/amplitude';
import 'lib/instrumentation';
import 'react-toastify/dist/ReactToastify.css';
import './assets/scss/style.scss';
import './assets/css/style.css';
import './utils/observability/webVitals';
import { AppVersionUpdateListener } from 'components/molecules/AppVersionUpdateListener';
import { emitFatalLog } from 'utils/observability/logger';
import { AnyValue } from '@opentelemetry/api-logs';
// Commented out for now as it is not working as expected
// import { NoInternetConnection } from 'components/molecules/NoInternetConnection';

export const store = configureStore();

function App() {
  useEffect(() => {
    initAmplitude();

    /**
     * Dynamic imports crash handler.
     *
     * Fires when a Vite-preloaded JS chunk is missing (e.g. stale cached index.html
     * referencing a chunk that was renamed after a deployment). We force a hard,
     * cache-busting reload so the browser fetches fresh HTML with current chunk hashes.
     *
     * sessionStorage guard prevents an infinite reload loop in the unlikely case where
     * the server itself is returning invalid JS (rather than a stale-cache mismatch).
     */
    const PRELOAD_RELOAD_KEY = 'vite_preload_error_reloaded';

    // Clear the guard on successful boot so future deployments can still auto-reload.
    sessionStorage.removeItem(PRELOAD_RELOAD_KEY);

    const handlePreloadError = (event: Event) => {
      emitFatalLog({
        body: {
          message: 'Vite Preload Error',
          data: event as unknown as AnyValue,
        },
        attrs: {
          source: 'ViteCrash',
        },
      });

      if (sessionStorage.getItem(PRELOAD_RELOAD_KEY)) {
        // Already attempted a cache-busting reload — don't loop.
        // The AppVersionUpdateListener toast will prompt the user manually.
        return;
      }

      sessionStorage.setItem(PRELOAD_RELOAD_KEY, '1');
      // Navigate to the same path with a cache-busting param to force the
      // browser to bypass its cached index.html and fetch fresh HTML + chunks.
      const url = new URL(window.location.href);
      url.searchParams.set('_cb', String(Date.now()));
      window.location.href = url.toString();
    };

    window.addEventListener('vite:preloadError', handlePreloadError);
    return () =>
      window.removeEventListener('vite:preloadError', handlePreloadError);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Commented out for now as it is not working as expected 
      <NoInternetConnection /> */}
      <Provider store={store}>
        <UserProvider>
          <ErrorBoundary>
            <WebSocketProvider url={`${WS_API_URL}/ws/download/`}>
              <ErrorBoundary>
                <RouterProvider router={router} />

                <ToastContainer
                  position="top-right"
                  autoClose={3000}
                  hideProgressBar={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss={false}
                  draggable
                  theme="light"
                  pauseOnHover
                  transition={Zoom}
                  limit={2}
                />

                <ErrorBoundary height={100}>
                  <SubscriptionExpire />
                  <AppVersionUpdateListener />
                  <FullScreenLoader />
                  <ScriptLoader />
                </ErrorBoundary>
              </ErrorBoundary>
            </WebSocketProvider>
          </ErrorBoundary>
        </UserProvider>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
